import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Server, CheckCircle2, XCircle, FileCheck, Database, Key } from 'lucide-react';
/*
  SEO SPECIFICATION:
  Title: MindStream.ing Security — Enterprise-Grade Protection Built on Google Cloud
  Meta Desc: Enterprise-grade security built on Google Cloud Platform. Your data is encrypted, monitored, and never shared. SOC2 aligned. GDPR/CCPA ready.
  Primary KW: AI automation security compliance
*/
export function Security() {
  return <main className="pt-20" data-id="element-2242">
      {/* Hero */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 px-6 bg-hero-gradient overflow-hidden" data-id="element-2243">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-2244" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-2245" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-2246" />

        <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-2247">
          <span className="eyebrow" data-id="element-2248">Security & Trust</span>
          <h1 className="font-poppins font-extrabold text-5xl md:text-6xl leading-[1.1] mb-6" data-id="element-2249">
            Your Business Data.
            <br data-id="element-2250" />
            <span className="text-gradient" data-id="element-2251">Protected. Always.</span>
          </h1>
          <p className="text-lg md:text-xl text-steel mb-10 leading-relaxed max-w-2xl mx-auto" data-id="element-2252">
            Enterprise-grade security built on Google Cloud Platform. Your data
            is encrypted, monitored, and never shared.
          </p>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-2253" />

      {/* Trust Badges */}
      <section className="py-12 bg-dark-bg relative" data-id="element-2254">
        <div className="inner-container relative z-10" data-id="element-2255">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-2256">
            {[{
            title: 'SOC2 Aligned',
            icon: <FileCheck size={24} data-id="element-2257" />
          }, {
            title: 'GDPR Ready',
            icon: <Globe size={24} data-id="element-2258" />
          }, {
            title: 'CCPA Compliant',
            icon: <Shield size={24} data-id="element-2259" />
          }, {
            title: '256-bit Encryption',
            icon: <Lock size={24} data-id="element-2260" />
          }].map((badge, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex flex-col items-center justify-center gap-3 p-6 bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm rounded-xl border border-white/10 hover:border-teal/30 hover:shadow-[0_0_20px_rgba(7,133,183,0.1)] transition-all" data-id="element-2261">
                <div className="text-teal bg-teal/10 p-3 rounded-full shadow-[0_0_15px_rgba(7,133,183,0.2)]" data-id="element-2262">
                  {badge.icon}
                </div>
                <span className="font-poppins font-semibold text-sm text-center" data-id="element-2263">
                  {badge.title}
                </span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-2264" />

      {/* Infrastructure */}
      <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-2265">
        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/10 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-2266" />

        <div className="inner-container relative z-10" data-id="element-2267">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-2268">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-2269">
              <span className="eyebrow" data-id="element-2270">Infrastructure</span>
              <h2 className="h2-title" data-id="element-2271">Built on Google Cloud</h2>
              <p className="text-steel mb-6 leading-relaxed" data-id="element-2272">
                MindStream.ing is hosted securely on Google Cloud Platform (GCP
                us-central1), leveraging the same infrastructure that powers
                Google's own products.
              </p>
              <ul className="space-y-4" data-id="element-2273">
                <li className="flex items-start gap-3" data-id="element-2274">
                  <Server className="text-teal shrink-0 mt-1" size={20} data-id="element-2275" />
                  <div data-id="element-2276">
                    <h4 className="font-bold text-white" data-id="element-2277">US-Based Hosting</h4>
                    <p className="text-sm text-steel" data-id="element-2278">
                      All primary data is stored and processed in GCP
                      us-central1.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-2279">
                  <Lock className="text-teal shrink-0 mt-1" size={20} data-id="element-2280" />
                  <div data-id="element-2281">
                    <h4 className="font-bold text-white" data-id="element-2282">
                      End-to-End Encryption
                    </h4>
                    <p className="text-sm text-steel" data-id="element-2283">
                      Data is encrypted at rest (AES-256) and in transit (TLS
                      1.3).
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-2284">
                  <Database className="text-teal shrink-0 mt-1" size={20} data-id="element-2285" />
                  <div data-id="element-2286">
                    <h4 className="font-bold text-white" data-id="element-2287">99.9% Uptime SLA</h4>
                    <p className="text-sm text-steel" data-id="element-2288">
                      Redundant architecture ensures your business never stops
                      running.
                    </p>
                  </div>
                </li>
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-[0_0_40px_rgba(0,0,0,0.5)] relative overflow-hidden" data-id="element-2289">
              <div className="absolute top-0 right-0 w-64 h-64 bg-teal/20 rounded-full blur-3xl pointer-events-none" data-id="element-2290"></div>
              <h3 className="font-poppins font-bold text-xl mb-6 relative z-10" data-id="element-2291">
                Access Controls
              </h3>
              <ul className="space-y-6 relative z-10" data-id="element-2292">
                <li className="flex items-center gap-4 border-b border-white/10 pb-4" data-id="element-2293">
                  <div className="bg-purple/20 p-2 rounded-lg text-purple" data-id="element-2294">
                    <Key size={20} data-id="element-2295" />
                  </div>
                  <div data-id="element-2296">
                    <div className="font-semibold" data-id="element-2297">
                      Role-Based Access (RBAC)
                    </div>
                    <div className="text-xs text-steel" data-id="element-2298">
                      Granular permissions for your team
                    </div>
                  </div>
                </li>
                <li className="flex items-center gap-4 border-b border-white/10 pb-4" data-id="element-2299">
                  <div className="bg-purple/20 p-2 rounded-lg text-purple" data-id="element-2300">
                    <Shield size={20} data-id="element-2301" />
                  </div>
                  <div data-id="element-2302">
                    <div className="font-semibold" data-id="element-2303">
                      Two-Factor Authentication
                    </div>
                    <div className="text-xs text-steel" data-id="element-2304">
                      Enforced 2FA for all admin accounts
                    </div>
                  </div>
                </li>
                <li className="flex items-center gap-4" data-id="element-2305">
                  <div className="bg-purple/20 p-2 rounded-lg text-purple" data-id="element-2306">
                    <FileCheck size={20} data-id="element-2307" />
                  </div>
                  <div data-id="element-2308">
                    <div className="font-semibold" data-id="element-2309">Audit Logging</div>
                    <div className="text-xs text-steel" data-id="element-2310">
                      Track every action taken by users and MISTR
                    </div>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-2311" />

      {/* Data Handling */}
      <section className="section-padding bg-dark-bg relative" data-id="element-2312">
        <div className="inner-container relative z-10" data-id="element-2313">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2314">
            <span className="eyebrow" data-id="element-2315">Data Privacy</span>
            <h2 className="h2-title" data-id="element-2316">What We Store (And What We Don't)</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-2317">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-teal/10 to-transparent backdrop-blur-sm border border-teal/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(7,133,183,0.1)]" data-id="element-2318">
              <div className="flex items-center gap-3 mb-6" data-id="element-2319">
                <CheckCircle2 className="text-teal drop-shadow-[0_0_8px_rgba(7,133,183,0.5)]" size={28} data-id="element-2320" />
                <h3 className="font-poppins font-bold text-xl" data-id="element-2321">
                  What MR Stores
                </h3>
              </div>
              <p className="text-steel text-sm mb-6" data-id="element-2322">
                Data required to run your business operations effectively.
              </p>
              <ul className="space-y-3" data-id="element-2323">
                <li className="flex items-center gap-2 text-sm" data-id="element-2324">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-2325"></div>{' '}
                  Business context & brand voice
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2326">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-2327"></div> Task
                  data and project status
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2328">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-2329"></div>{' '}
                  Communication logs (emails/SMS sent via MR)
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2330">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-2331"></div> CRM
                  contact details
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-gradient-to-br from-red-cta/10 to-transparent backdrop-blur-sm border border-red-cta/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(255,0,10,0.1)]" data-id="element-2332">
              <div className="flex items-center gap-3 mb-6" data-id="element-2333">
                <XCircle className="text-red-cta drop-shadow-[0_0_8px_rgba(255,0,10,0.5)]" size={28} data-id="element-2334" />
                <h3 className="font-poppins font-bold text-xl" data-id="element-2335">
                  What MR NEVER Stores
                </h3>
              </div>
              <p className="text-steel text-sm mb-6" data-id="element-2336">
                Sensitive data that MR does not need to retain.
              </p>
              <ul className="space-y-3" data-id="element-2337">
                <li className="flex items-center gap-2 text-sm" data-id="element-2338">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-cta" data-id="element-2339"></div>{' '}
                  Raw passwords or credentials
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2340">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-cta" data-id="element-2341"></div>{' '}
                  Full payment information (CC numbers)
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2342">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-cta" data-id="element-2343"></div>{' '}
                  Personal health data (PHI)
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2344">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-cta" data-id="element-2345"></div>{' '}
                  Unrelated personal communications
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-2346" />

      {/* Enterprise Add-ons */}
      <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-2347">
        {/* Ambient Glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple/10 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-2348" />

        <div className="inner-container max-w-4xl mx-auto relative z-10" data-id="element-2349">
          <div className="bg-gradient-to-br from-purple/10 to-teal/10 backdrop-blur-md border border-purple/30 rounded-2xl p-10 text-center shadow-[0_0_40px_rgba(124,58,237,0.15)]" data-id="element-2350">
            <h3 className="font-poppins font-bold text-2xl mb-4" data-id="element-2351">
              Enterprise Security Add-ons
            </h3>
            <p className="text-steel mb-8 max-w-2xl mx-auto" data-id="element-2352">
              Need stricter compliance? Our Enterprise plan includes advanced
              security features for larger organizations.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8" data-id="element-2353">
              <span className="bg-white/5 px-4 py-2 rounded-lg text-sm" data-id="element-2354">
                Custom Data Retention
              </span>
              <span className="bg-white/5 px-4 py-2 rounded-lg text-sm" data-id="element-2355">
                Dedicated Infrastructure
              </span>
              <span className="bg-white/5 px-4 py-2 rounded-lg text-sm" data-id="element-2356">
                Annual Security Review
              </span>
              <span className="bg-white/5 px-4 py-2 rounded-lg text-sm" data-id="element-2357">
                BAA Available
              </span>
            </div>
            <button className="btn-primary" data-id="element-2358">
              Questions about security? Talk to MR →
            </button>
          </div>
        </div>
      </section>
    </main>;
}
// Helper component for the Globe icon that was missing from lucide-react import
function Globe(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2359">
      <circle cx="12" cy="12" r="10" data-id="element-2360" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" data-id="element-2361" />
      <path d="M2 12h20" data-id="element-2362" />
    </svg>;
}